import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Spinner } from "@/components/common";
import { RefreshCcw, ArrowRight, CalendarClock } from "lucide-react";

// Shared component: 30-day rolling list of upcoming replacement lessons.
// Rendered on both Parent Dashboard and Teacher Dashboard so both parties
// see the exact same info about accepted replacements coming up. The window
// (7 / 14 / 30 days) is selectable and persisted per-user in localStorage.
const WINDOW_OPTIONS = [7, 14, 30];
const STORAGE_KEY = "upcoming-repl-days";

export default function UpcomingReplacementsCard({ role /* "parent" | "teacher" */ }) {
  const [days, setDays] = useState(() => {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    return WINDOW_OPTIONS.includes(stored) ? stored : 30;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const endpoint = role === "teacher"
    ? "/teacher/upcoming-replacements"
    : "/parent/upcoming-replacements";

  useEffect(() => {
    setLoading(true);
    api.get(endpoint, { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
    localStorage.setItem(STORAGE_KEY, String(days));
  }, [endpoint, days]);

  const items = data?.items || [];
  const grouped = useMemo(() => {
    const by = {};
    items.forEach((it) => { (by[it.date] ??= []).push(it); });
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);
  const link = role === "teacher" ? "/teacher/reschedule" : "/parent/reschedule";

  return (
    <div className="rounded-lg border bg-card p-5" data-testid="upcoming-replacements-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <RefreshCcw className="h-4 w-4 text-primary" />
          <h3 className="text-base font-semibold">Upcoming replacements</h3>
          {items.length > 0 && (
            <span className="text-[10px] font-mono bg-primary/10 text-primary rounded-full px-2 py-0.5"
              data-testid="upcoming-replacements-count">
              {items.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 rounded-md border bg-muted/30 p-0.5">
          {WINDOW_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`text-[11px] font-medium px-2 py-0.5 rounded transition ${
                days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`upcoming-replacements-days-${d}`}
            >{d}d</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="py-6 flex justify-center"><Spinner /></div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground" data-testid="upcoming-replacements-empty">
          <CalendarClock className="h-6 w-6 mx-auto mb-2 opacity-40" />
          No confirmed replacements in the next {days} days.
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {grouped.map(([date, rows]) => (
            <div key={date} className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {new Date(date + "T00:00:00").toLocaleDateString(undefined,
                  { weekday: "short", day: "numeric", month: "short" })}
              </p>
              {rows.map((r) => (
                <div key={r.id}
                  className="rounded-md border bg-[#B8860B]/5 border-[#B8860B]/30 p-2.5 text-xs"
                  data-testid={`upcoming-replacement-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">
                      {r.start_time}–{r.end_time}
                      {role === "parent" && r.student_name ? ` · ${r.student_name}` : ""}
                      {role === "teacher" && r.student_name ? ` · ${r.student_name}` : ""}
                    </p>
                    <span className="text-[10px] rounded-full px-2 py-0.5 bg-[#B8860B]/15 text-[#8B6914] font-medium">
                      Replacement
                    </span>
                  </div>
                  <p className="text-muted-foreground mt-0.5">
                    {r.course_name}
                    {r.studio_name ? ` · ${r.studio_name}` : ""}
                    {role === "parent" && r.teacher_name ? ` · ${r.teacher_name}` : ""}
                  </p>
                  {r.original_date && (
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                      was {new Date(r.original_date + "T00:00:00").toLocaleDateString(undefined,
                        { weekday: "short", day: "numeric", month: "short" })}
                      {r.original_start_time && ` ${r.original_start_time}`}
                    </p>
                  )}
                  {r.is_holiday_mirror && r.holiday_name && (
                    <p className="text-[10px] font-medium text-[#8B6914] mt-0.5">
                      🎌 Holiday mirror-slot · {r.holiday_name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <Link to={link} className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-3"
        data-testid="upcoming-replacements-view-all">
        View all reschedules <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
